import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight, Sparkles, Shirt } from 'lucide-react';

const LookDetailView = ({ look, wardrobeItems, onClose, onImageClick }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  const lookItemIds = look.wardrobe_item_ids || [];
  const lookItems = lookItemIds
    .map(id => wardrobeItems.find(item => item.id === id))
    .filter(Boolean);

  const images = look.look_images || [];
  
  const nextImage = () => {
    if (images.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
    }
  };
  
  const prevImage = () => {
    if (images.length > 1) {
      setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
    }
  };

  return (
    <div
      onClick={onClose}
      data-testid="look-detail-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--space-l)',
        zIndex: 1500
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="look-detail-content"
        style={{
          backgroundColor: 'var(--white)',
          maxWidth: '900px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Header */}
        <div style={{
          padding: 'var(--space-l)',
          borderBottom: '1px solid var(--soft-gray)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', fontWeight: 300 }}>
            {look.name}
          </h2>
          <button
            onClick={onClose}
            data-testid="look-detail-close-btn"
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
          >
            <X size={24} />
          </button>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap' }}>
          {/* Left side - Images */}
          <div style={{ flex: '1 1 400px', minWidth: '300px' }}>
            {images.length > 0 ? (
              <div style={{ position: 'relative' }}>
                <div 
                  style={{ 
                    height: '400px', 
                    backgroundColor: 'var(--cream)',
                    cursor: 'zoom-in'
                  }}
                  onClick={() => onImageClick(images[currentImageIndex])}
                >
                  <img
                    src={images[currentImageIndex]}
                    alt={look.name}
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                </div>
                
                {images.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      data-testid="look-detail-prev-img"
                      style={{
                        position: 'absolute',
                        left: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        backgroundColor: 'rgba(255,255,255,0.9)',
                        border: 'none',
                        padding: '10px',
                        cursor: 'pointer',
                        borderRadius: '50%'
                      }}
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <button
                      onClick={nextImage}
                      data-testid="look-detail-next-img"
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        backgroundColor: 'rgba(255,255,255,0.9)',
                        border: 'none',
                        padding: '10px',
                        cursor: 'pointer',
                        borderRadius: '50%'
                      }}
                    >
                      <ChevronRight size={20} />
                    </button>
                    <div style={{
                      position: 'absolute',
                      bottom: '10px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      backgroundColor: 'rgba(0,0,0,0.6)',
                      color: 'white',
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '0.75rem'
                    }}>
                      {currentImageIndex + 1} / {images.length}
                    </div>
                  </>
                )}
                
                {images.length > 1 && (
                  <div style={{
                    display: 'flex',
                    gap: 'var(--space-xs)',
                    padding: 'var(--space-m)',
                    overflowX: 'auto'
                  }}>
                    {images.map((img, idx) => (
                      <div
                        key={idx}
                        onClick={() => setCurrentImageIndex(idx)}
                        style={{
                          width: '60px',
                          height: '60px',
                          flexShrink: 0,
                          cursor: 'pointer',
                          border: idx === currentImageIndex ? '2px solid var(--accent-gold)' : '2px solid transparent',
                          overflow: 'hidden'
                        }}
                      >
                        <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div style={{
                height: '300px',
                backgroundColor: 'var(--cream)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Sparkles size={48} style={{ color: 'var(--light-gray)' }} />
              </div>
            )}
          </div>

          {/* Right side - Details */}
          <div style={{ flex: '1 1 300px', padding: 'var(--space-xl)' }}>
            {look.description && (
              <div style={{ marginBottom: 'var(--space-l)' }}>
                <h4 style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  color: 'var(--light-gray)',
                  marginBottom: 'var(--space-s)'
                }}>
                  Описание
                </h4>
                <p style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.9375rem',
                  lineHeight: 1.6,
                  color: 'var(--charcoal)'
                }}>
                  {look.description}
                </p>
              </div>
            )}

            {look.event_types?.length > 0 && (
              <div style={{ marginBottom: 'var(--space-l)' }}>
                <h4 style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  color: 'var(--light-gray)',
                  marginBottom: 'var(--space-s)'
                }}>
                  Подходит для
                </h4>
                <div style={{ display: 'flex', gap: 'var(--space-s)', flexWrap: 'wrap' }}>
                  {look.event_types.map((event, idx) => (
                    <span key={idx} style={{
                      padding: '6px 14px',
                      backgroundColor: 'var(--cream)',
                      fontSize: '0.8125rem',
                      fontFamily: 'var(--font-body)'
                    }}>
                      {event}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Wardrobe Items */}
            <div>
              <h4 style={{
                fontFamily: 'var(--font-body)',
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                color: 'var(--light-gray)',
                marginBottom: 'var(--space-s)'
              }}>
                Вещи в образе ({lookItems.length})
              </h4>
              {lookItems.length > 0 ? (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
                  gap: 'var(--space-s)'
                }}>
                  {lookItems.map((item) => (
                    <div 
                      key={item.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => item.image_url && onImageClick(item.image_url)}
                    >
                      <div style={{
                        height: '80px',
                        backgroundColor: 'var(--cream)',
                        overflow: 'hidden'
                      }}>
                        {item.image_url ? (
                          <img
                            src={item.image_url}
                            alt={item.item_kind}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          <div style={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <Shirt size={20} style={{ color: 'var(--light-gray)' }} />
                          </div>
                        )}
                      </div>
                      <p style={{
                        fontFamily: 'var(--font-body)',
                        fontSize: '0.6875rem',
                        textAlign: 'center',
                        marginTop: '4px',
                        textTransform: 'capitalize',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {item.item_kind}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.875rem',
                  color: 'var(--mid-gray)',
                  fontStyle: 'italic'
                }}>
                  Нет привязанных вещей
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LookDetailView;
